import type { AxiosResponse } from 'axios'
import { useSignalR } from 'BrokerTsLib/src/context/SignalRProvider'
import type { Offer } from 'BrokerTsLib/src/dtos'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { QueryObserverResult, UseMutationResult, UseQueryResult } from '@tanstack/react-query'

import type { CalculateOffersRequest } from '../hooks/useOffer'

export const SortBy = {
  Price: 'price',
  Insurer: 'insurer',
  VariantPrice: 'variantPrice',
  VariantInsurer: 'variantInsurer'
}
export type SortBy = (typeof SortBy)[keyof typeof SortBy]

export interface IOffersContext<T extends Offer> {
  sortBy: SortBy
  offers: T[]
  offersIds: number[]
  isLoading: boolean
  calculatingOffersIds: number[]
  showAll: boolean
  setShowAll: (showAll: boolean) => void
  setSortBy: (data: SortBy) => void
  recalculateOffers: (offerIds?: number[], force?: boolean) => Promise<AxiosResponse<T[], any>>
  updateOffer: ((offer: T) => void)
  refetchOffers: () => Promise<QueryObserverResult<AxiosResponse<T[], any>, any>>
}

export const defaults: IOffersContext<Offer> = {
  sortBy: SortBy.Price,
  offers: [],
  offersIds: [],
  isLoading: false,
  calculatingOffersIds: [],
  showAll: true,
  setShowAll: () => { },
  setSortBy: () => { },
  recalculateOffers: () => { return null as any },
  updateOffer: () => { },
  refetchOffers: () => { return null as any }
}

export type BaseOffersProviderProps = {
  insuranceParamsId: string
  isContract: boolean
  /** vynuti prepocet nabidek pri prvni automaticke kalkulaci (prichod z K2 pres "Ulozit a spocitat nabidky") */
  forceCalculation?: boolean
  children: ReactNode
}

type OffersProviderProps<T extends Offer> = {
  signalRCalcFinishedEventName: string
  useGetOffers(paramsId: string, options?: GenericQueryOptions<T[]>): UseQueryResult<AxiosResponse<T[], any>, any>
  useCalculateOffers(): UseMutationResult<AxiosResponse<T[], any>, unknown, CalculateOffersRequest, unknown>
  context: React.Context<IOffersContext<T>>
} & BaseOffersProviderProps

export function OffersProvider<T extends Offer>({ children, insuranceParamsId, isContract, forceCalculation, useGetOffers, useCalculateOffers, context, signalRCalcFinishedEventName }: OffersProviderProps<T>) {
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.Price)
  const [showAll, setShowAll] = useState(false)
  const [calculatingOffersIds, setCalculatingOffersIds] = useState<number[]>([])
  const [offersIds, setOfferIds] = useState<number[]>([])
  const [offers, setOffers] = useState<T[]>([])
  const forceCalculationUsed = useRef(false)

  const signalR = useSignalR()
  const hubId = signalR?.connectionId ?? ''

  const { mutateAsync: calculateOffers, isPending: isCalculating } = useCalculateOffers()
  const { refetch: refetchOffers, isLoading: isLoadingOffers, isFetching: isFetchingOffers, data: offersData } = useGetOffers(insuranceParamsId, {
    enabled: false,
    retry: false,
    gcTime: 0,
  })

  useEffect(() => {
    const data = offersData?.data
    if (data) {
      setOfferIds(data.map(o => o.offerId) ?? [])
      if (isContract)
        setOffers(data)
    }
  }, [offersData])

  const calculate = (ids?: number[], force: boolean = false) => {
    setCalculatingOffersIds(ids ?? offers.map(x => x.offerId))
    return calculateOffers({ hubId, offerIds: ids, paramsId: insuranceParamsId, forceCalculation: force ?? false })
  }
  const updateOffer = (offer: T) => {
    setOffers(prev => {
      const index = prev.findIndex(x => x.offerId === offer.offerId)
      if (index >= 0) {
        const updated = [...prev]
        updated[index] = offer

        return updated
      }

      return [...prev, offer]
    })
  }

  // ziskanni / zajisteni vytvoreni offers
  useEffect(() => {
    refetchOffers()
  }, [])

  // ziskani kalkulaci
  useEffect(() => {
    if (!!hubId && offersIds.length > 0 && !isContract) {
      // force se uplatni pouze na prvni kalkulaci po vstupu na krok, dalsi behy uz jedou standardne
      const force = forceCalculation && !forceCalculationUsed.current
      forceCalculationUsed.current = true

      calculate(offersIds, force)
    }
  }, [hubId, offersIds, isContract])

  // kalkulace se vraci postupne pres signalR
  useEffect(() => {
    signalR?.on(signalRCalcFinishedEventName, (offer: T) => {
      setCalculatingOffersIds(prev => [...prev.filter(x => x != offer.offerId)])
      updateOffer(offer)
    })

    return () => {
      signalR?.off(signalRCalcFinishedEventName)
    }
  }, [signalR])

  return (
    <context.Provider value={{
      sortBy: sortBy,
      offers: offers ?? [],
      offersIds: offersIds ?? [],
      isLoading: isLoadingOffers || isFetchingOffers || (offers?.filter(x => x.included)?.length ?? 0) == 0 && isCalculating,
      calculatingOffersIds: calculatingOffersIds,
      showAll: showAll,
      setSortBy: setSortBy,
      recalculateOffers: (offersIds, force) => calculate(offersIds, force),
      updateOffer: updateOffer,
      setShowAll: setShowAll,
      refetchOffers: refetchOffers,
    }}>
      {children}
    </context.Provider>
  )
}

